"use client";

import { Dispatch, FC, SetStateAction, Suspense, use, useState } from "react";
import { observer } from "mobx-react";
import styles from "./styles/SchoolFeedDisplay.module.scss";
import {
  IFallbackData,
  ISchoolFeedUrlQueries,
} from "../../utils/SchoolBlocksUtilities";
import { useSchoolFeedInfiniteScroll } from "./hooks/useSchoolFeedInfiniteScroll";
import { IReactiveNewsContentItem } from "./SchoolFeedContentItem";
import { FetchMoreNewsButton } from "../blocks/TerracedGridTheme/NewsBlock/FetchMoreNewsButton";
import SchoolFeedList from "./ContentPostDisplay/SchoolFeedList";
import PlaceholderNewsItem from "../../assets/images/placeholders/placeholder-news-item.svg";
import { useStores } from "../../stores/useStores";
import { Box, Button, Text } from "@radix-ui/themes";
import HorizontalScrollContainer from "../misc/HorizontalScrollContainer";
import { ContactCardOrganization, LeaderboardEntry } from "./SchoolFeedDisplay";

function OrganizationFilter(props: {
  widgetPromises: {
    leaderboard: Promise<LeaderboardEntry[]>;
    contactCard: Promise<ContactCardOrganization>;
    contributingOrgs: Promise<IOrganizationBlockObj[]>;
  };
  organizationIdFilter: string;
  setOrganizationIdFilter: Dispatch<SetStateAction<string>>;
}) {
  const { organizationStore } = useStores();
  const contributingOrgs = use(props.widgetPromises.contributingOrgs);

  return (
    <HorizontalScrollContainer
      scrollItemClassName={"scroll-item"}
      scrollContainerClassName={styles.horizontalScrollContentContainer}
      className={styles.horizontalScrollContainer}
    >
      {contributingOrgs.map((org, idx) => {
        return (
          <Button
            className={"scroll-item"}
            key={org.id}
            onClick={() => {
              if (props.organizationIdFilter === org.id) {
                props.setOrganizationIdFilter(
                  organizationStore.currentOrganization.id,
                );
              } else {
                props.setOrganizationIdFilter(org.id);
              }
            }}
            data-index={idx}
            color={props.organizationIdFilter === org.id ? undefined : "gray"}
          >
            {org.blockModel.title}
          </Button>
        );
      })}
    </HorizontalScrollContainer>
  );
}

const SchoolFeedNewsDisplay: FC<{
  urlQuery: ISchoolFeedUrlQueries;
  initialNewsPromise: Promise<IFallbackData>;
  widgetPromises: {
    leaderboard: Promise<LeaderboardEntry[]>;
    contactCard: Promise<ContactCardOrganization>;
    contributingOrgs: Promise<IOrganizationBlockObj[]>;
  };
}> = observer((props) => {
  const { organizationStore } = useStores();
  const [organizationIdFilter, setOrganizationIdFilter] = useState(
    organizationStore.currentOrganization.id,
  );
  const {
    contentItems,
    totalContentItems,
    getItemAtIndex,
    showViewMore,
    isValidating,
    setSize,
    mutate,
    isLoading,
  } = useSchoolFeedInfiniteScroll(
    "news-schoolfeed",
    {
      is_news: true,
      page_size: 10,
      organization_id: organizationIdFilter,
    },
    use(props.initialNewsPromise),
  );

  async function handleDeleteContentItem() {
    await mutate();
  }

  const fetchNews = async () => {
    await setSize((s) => s + 1);
  };

  return (
    <div className={styles.newsFeedContainer}>
      {organizationStore.currentOrganization.id ===
        organizationStore.organization.id && (
        <Suspense>
          <OrganizationFilter
            widgetPromises={props.widgetPromises}
            organizationIdFilter={organizationIdFilter}
            setOrganizationIdFilter={setOrganizationIdFilter}
          />
        </Suspense>
      )}
      {isLoading ? (
        <SchoolFeedNewsLoading />
      ) : (
        <>
          {contentItems.length > 0 ? (
            <Box pb={"2"}>
              <SchoolFeedList
                showMedia={true}
                allowCuration={true}
                content={contentItems as IReactiveNewsContentItem[]}
                totalItemCount={totalContentItems}
                handleDelete={handleDeleteContentItem}
                className={styles.schoolFeedList}
                getItemAtIndex={getItemAtIndex}
              />
            </Box>
          ) : (
            <Box style={{ textAlign: "center" }} py={"4"}>
              <Text size={"4"} weight={"medium"}>
                No content found.
              </Text>
            </Box>
          )}
          {showViewMore && (
            <FetchMoreNewsButton
              onClick={fetchNews}
              isLoading={isValidating}
              contentType={"news"}
            />
          )}
        </>
      )}
    </div>
  );
});

export default SchoolFeedNewsDisplay;

export function SchoolFeedNewsLoading() {
  return (
    <div className={styles.newsLoading}>
      {/*@ts-ignore*/}
      <img src={PlaceholderNewsItem} alt="Placeholder News Item" />
      {/*@ts-ignore*/}
      <img src={PlaceholderNewsItem} alt="Placeholder News Item" />
      {/*@ts-ignore*/}
      <img src={PlaceholderNewsItem} alt="Placeholder News Item" />
      {/*@ts-ignore*/}
      <img src={PlaceholderNewsItem} alt="Placeholder News Item" />
    </div>
  );
}
